import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Heart,
  Globe,
  Award,
  GraduationCap,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const AboutSection = () => {
  const { t, i18n } = useTranslation();
  const dir = i18n.dir();
  const isRTL = dir === "rtl";

  /* --------- SLIDES PRO (carrousel) --------- */
  const proSlides = useMemo(
    () => [
      {
        icon: Award,
        tone: "sage",
        title: t("about.pro.cards.cert.title"),
        bullets: [
          t("about.pro.cards.cert.items.0"),
          t("about.pro.cards.cert.items.1"),
          t("about.pro.cards.cert.items.2"),
          t("about.pro.cards.cert.items.3"),
        ],
      },
      {
        icon: GraduationCap,
        tone: "taupe",
        title: t("about.pro.cards.academic.title"),
        bullets: [
          t("about.pro.cards.academic.items.0"),
          t("about.pro.cards.academic.items.1"),
          t("about.pro.cards.academic.items.2"),
          t("about.pro.cards.academic.items.3"),
        ],
      },
      {
        icon: Briefcase,
        tone: "choco",
        title: t("about.pro.cards.expertise.title"),
        bullets: [
          t("about.pro.cards.expertise.items.0"),
          t("about.pro.cards.expertise.items.1"),
          t("about.pro.cards.expertise.items.2"),
          t("about.pro.cards.expertise.items.3"),
        ],
      },
    ],
    [t]
  );

  const [proIndex, setProIndex] = useState(0);
  useEffect(() => {
    const timer = setInterval(
      () => setProIndex((n) => (n + 1) % proSlides.length),
      8000
    );
    return () => clearInterval(timer);
  }, [proSlides.length]);

  const goPro = (step: number) =>
    setProIndex((n) => (n + step + proSlides.length) % proSlides.length);

  const [showFullText, setShowFullText] = useState(false);

  return (
    <section
      id="apropos"
      dir={dir}
      className="relative overflow-hidden bg-gradient-to-b from-soft-gray via-cream-white to-soft-gray"
    >
      <div className="absolute inset-0 pointer-events-none">
        <svg className="absolute inset-0 w-full h-full opacity-5">
          <defs>
            <radialGradient id="g" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#C4B5A0" />
              <stop offset="100%" stopColor="#D4C4B0" />
            </radialGradient>
          </defs>
          <circle cx="180" cy="180" r="110" fill="url(#g)" />
          <circle cx="1620" cy="320" r="150" fill="url(#g)" opacity=".7" />
          <circle cx="980" cy="720" r="220" fill="url(#g)" opacity=".4" />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* ---------- EN-TÊTE: La femme derrière + nom ---------- */}
        <header className="text-center mb-12">
          <div className="inline-flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-warm-sage" />
            <Heart className="h-6 w-6 text-warm-sage" />
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-warm-sage" />
          </div>

          {/* Titre un peu plus discret */}
          <h2 className="font-serif font-light tracking-tight text-charcoal text-3xl md:text-4xl lg:text-[40px] leading-snug">
            {t("La femme derrière Mind’UP")}
          </h2>

          {/* Nom artistique */}
          <p className="mt-3 text-[15px] md:text-base tracking-[0.22em] uppercase text-warm-gray">
            Sanae <span className="font-semibold">Ait BALLA</span>
          </p>

          <div className="mt-6 w-28 h-[2px] bg-gradient-to-r from-warm-sage to-soft-taupe mx-auto rounded-full" />
        </header>

        {/* ---------- SECTION FEMME (portrait + texte) ---------- */}
        <div className="grid lg:grid-cols-2 gap-20 items-start mb-12">
          <div className="relative group">
            <div className="relative overflow-hidden rounded-3xl shadow-2xl transform group-hover:scale-105 transition-all duration-700">
              <img
                src="/Images/blackwhite.jpg"
                alt={t("about.woman.imageAlt")}
                className="w-full h-[560px] object-cover transform group-hover:scale-110 transition-transform duration-1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 via-transparent to-warm-sage/20" />
            </div>

            <div className="absolute -top-8 -right-8 w-24 h-24 bg-warm-sage/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-soft-taupe/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700 delay-200" />
          </div>

          <div className="space-y-12">
            <div className="text-center relative">
              <h3 className="text-2xl md:text-3xl font-serif font-light text-charcoal mb-6 tracking-tight">
                {t("about.woman.title", "La femme derrière Mind’UP")}
              </h3>
              <div className="w-24 h-[2px] bg-gradient-to-r from-warm-sage to-soft-taupe mx-auto rounded-full mb-10" />
            </div>

            <div className="max-w-3xl mx-auto text-center space-y-8">
              <p className="text-lg md:text-xl lg:text-[22px] font-serif font-light text-charcoal leading-relaxed">
                {t("about.woman.opening.line1")}
                <br />
                <span className="text-warm-sage">
                  {t("about.woman.opening.line2")}
                </span>
              </p>

              <div className="space-y-3">
                <p className="text-base md:text-lg text-charcoal font-light">
                  {t("about.woman.opening.line3")}
                </p>
                <p className="text-base md:text-lg text-warm-sage font-medium">
                  {t("about.woman.opening.line4")}
                </p>
              </div>

              <div className="bg-warm-sage/5 p-6 rounded-2xl border-l-4 border-warm-sage max-w-2xl mx-auto">
                <p className="text-lg text-charcoal italic font-light leading-relaxed">
                  “{t("about.woman.quote.p1")}
                  <br />
                  {t("about.woman.quote.p2")}{" "}
                  <span className="text-warm-sage font-medium">
                    {t("about.woman.quote.p3")}
                  </span>
                  ”
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-10 pt-4">
                <div>
                  <h4 className="text-lg md:text-xl font-serif font-medium text-charcoal mb-4">
                    {t("about.woman.mother.title")}
                  </h4>
                  <p className="text-charcoal leading-relaxed font-light">
                    <span className="text-warm-sage font-medium">
                      {t("about.woman.mother.p1.highlight")}
                    </span>{" "}
                    {t("about.woman.mother.p1.rest")}
                  </p>
                </div>
                <div>
                  <h4 className="text-lg md:text-xl font-serif font-medium text-charcoal mb-4">
                    {t("about.woman.spouse.title")}
                  </h4>
                  <p className="text-charcoal leading-relaxed font-light">
                    {t("about.woman.spouse.p1.part1")}{" "}
                    <span className="text-soft-taupe font-medium">
                      {t("about.woman.spouse.p1.part2")}
                    </span>{" "}
                    {t("about.woman.spouse.p1.part3")}
                  </p>
                  <p className="text-warm-gray italic text-sm mt-2">
                    {t("about.woman.spouse.p2")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- PRO : TAGLINE + CARROUSEL (minimal, coloré léger) ---------- */}
        <section className="bg-white/70 backdrop-blur-sm rounded-3xl border border-warm-sage/20 p-8 md:p-10 mb-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-10 bg-gradient-to-r from-transparent to-warm-sage" />
              <Briefcase className="h-5 w-5 text-warm-sage" />
              <div className="h-px w-10 bg-gradient-to-l from-transparent to-warm-sage" />
            </div>
            <p className="text-lg md:text-xl font-serif font-light text-charcoal italic">
              {t("about.pro.tagline")}
            </p>
          </div>

          <div className="relative max-w-3xl mx-auto">
            {/* carte */}
            <article
              className={[
                "rounded-2xl p-7 md:p-8 shadow-md transition-all duration-500",
                proSlides[proIndex].tone === "sage"
                  ? "bg-gradient-to-br from-warm-sage/10 to-transparent border border-warm-sage/30"
                  : proSlides[proIndex].tone === "taupe"
                  ? "bg-gradient-to-br from-soft-taupe/10 to-transparent border border-soft-taupe/30"
                  : "bg-gradient-to-br from-rich-chocolate/10 to-transparent border border-rich-chocolate/25",
              ].join(" ")}
            >
              <div className="flex items-start gap-3 mb-5">
                <div
                  className={[
                    "p-2.5 rounded-lg",
                    proSlides[proIndex].tone === "sage"
                      ? "bg-warm-sage/20"
                      : proSlides[proIndex].tone === "taupe"
                      ? "bg-soft-taupe/20"
                      : "bg-rich-chocolate/20",
                  ].join(" ")}
                >
                  {React.createElement(proSlides[proIndex].icon, {
                    className:
                      "h-5 w-5 " +
                      (proSlides[proIndex].tone === "sage"
                        ? "text-warm-sage"
                        : proSlides[proIndex].tone === "taupe"
                        ? "text-soft-taupe"
                        : "text-rich-chocolate"),
                  })}
                </div>
                <h4 className="text-lg md:text-xl font-serif font-medium text-charcoal leading-snug">
                  {proSlides[proIndex].title}
                </h4>
              </div>

              <ul className="space-y-2 text-[15px] md:text-base text-charcoal/90 leading-relaxed font-light">
                {proSlides[proIndex].bullets.map((b, i) => (
                  <li key={i} className="flex">
                    <span className="text-warm-sage font-medium mr-2">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </article>

            {/* nav */}
            <div className="mt-7 flex items-center justify-between">
              <button
                onClick={() => goPro(isRTL ? 1 : -1)}
                aria-label="Précédent"
                className="carousel-nav-btn"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>

              <div className="flex items-center gap-3">
                {proSlides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setProIndex(i)}
                    className={`paper-dot ${
                      i === proIndex ? "paper-dot--active" : ""
                    }`}
                    aria-label={`Aller à ${i + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={() => goPro(isRTL ? -1 : 1)}
                aria-label="Suivant"
                className="carousel-nav-btn"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            <p className="text-center mt-5 text-xs text-warm-gray/80 italic">
              {t("about.pro.footer")}
            </p>
          </div>
        </section>

        {/* ---------- ENTRE LES MONDES (déplacé après PRO) ---------- */}
        <section className="grid lg:grid-cols-2 gap-20 items-center mb-12">
          <div>
            <h3 className="text-3xl md:text-4xl font-serif font-light text-charcoal mb-6 tracking-tight flex items-center">
              <div className="bg-warm-sage/20 p-3 rounded-xl mr-4">
                <Globe className="h-7 w-7 text-warm-sage" />
              </div>
              {t("about.betweenWorlds.title")}
            </h3>

            <div className="space-y-7">
              <div>
                <p className="text-lg text-charcoal leading-relaxed mb-3">
                  {t("about.betweenWorlds.p1")}
                </p>
                <div className="w-16 h-px bg-warm-sage/40" />
              </div>

              <div>
                <p className="text-base text-charcoal leading-relaxed mb-3">
                  {t("about.betweenWorlds.p2")}
                </p>
                <div className="w-16 h-px bg-soft-taupe/40" />
              </div>
            </div>
          </div>

          <div className="relative group">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img
                src="/Images/sofa.jpg"
                alt={t("about.betweenWorlds.imageAlt")}
                className="w-full h-96 object-cover transform group-hover:scale-110 transition-transform duration-1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 via-transparent to-warm-sage/20" />
            </div>
          </div>
        </section>

        {/* ---------- ALCHEMIE (inchangé) ---------- */}
        <section className="w-full bg-gradient-to-r from-rich-chocolate/5 via-warm-sage/10 to-soft-taupe/5 py-10 px-6 md:px-10 rounded-3xl">
          <h4 className="text-3xl md:text-4xl font-serif font-light text-charcoal mb-10 tracking-tight">
            {t("about.alchemy.title")}
          </h4>

          <div className="max-w-5xl space-y-7 leading-relaxed font-light text-charcoal">
            <p>
              {t("about.alchemy.p1.part1")}{" "}
              <span className="text-warm-sage font-medium italic">
                {t("about.alchemy.p1.part2")}
              </span>{" "}
              {t("about.alchemy.p1.part3")}{" "}
              <span className="text-warm-sage font-medium">
                {t("about.alchemy.p1.part4")}
              </span>{" "}
              {t("about.alchemy.p1.part5")}
            </p>

            {showFullText && (
              <>
                <p>
                  {t("about.alchemy.p2.part1")}{" "}
                  <span className="text-rich-chocolate font-medium italic">
                    {t("about.alchemy.p2.part2")}
                  </span>{" "}
                  {t("about.alchemy.p2.part3")}{" "}
                  <span className="text-rich-chocolate font-medium">
                    « {t("about.alchemy.p2.part4")} »
                  </span>{" "}
                  {t("about.alchemy.p2.part5")}
                </p>

                <p className="text-warm-gray italic">{t("about.alchemy.p3")}</p>

                <p>
                  {t("about.alchemy.p4.part1")}{" "}
                  <span className="text-warm-sage font-medium italic">
                    {t("about.alchemy.p4.part2")}
                  </span>
                </p>

                <div className="bg-warm-sage/10 p-8 rounded-2xl border border-warm-sage/20 my-8">
                  <blockquote className="text-lg md:text-xl font-serif italic">
                    “{t("about.alchemy.quote.p1")}{" "}
                    <span className="text-warm-sage font-medium">
                      {t("about.alchemy.quote.p2")}
                    </span>
                    ”
                  </blockquote>
                </div>

                <p className="text-warm-gray italic">
                  {t("about.alchemy.p5")}
                </p>
              </>
            )}
          </div>

          <div className="text-center mt-6">
            <button
              onClick={() => setShowFullText((s) => !s)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-warm-sage/10 hover:bg-warm-sage/20 text-warm-sage border border-warm-sage/30 hover:border-warm-sage/50 rounded-xl transition-all duration-300"
            >
              <span className="font-medium">
                {showFullText ? t("about.readLess") : t("about.readMore")}
              </span>
              {showFullText ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </div>
        </section>
      </div>
    </section>
  );
};

export default AboutSection;
